// ─────────────────────────────────────────────────────────────────────────────
// Frontend Model — Device.js
// ─────────────────────────────────────────────────────────────────────────────

const generateDeviceKey = () => {
  const year = new Date().getFullYear(); // 2026
  const random = Math.floor(Math.random() * 1e14).toString().padStart(14, '0');
  return `KEY${year}${random}`; // KEY2026 + 14 digits = 21 chars total → trim to 20
  // Actually: KEY(4) + year(4) + digits = too long. Do: KEY + year(4) + 13 digits = 20
};

const generateId = () => {
  const year = new Date().getFullYear();
  const digits = Math.floor(Math.random() * 1e13).toString().padStart(13, '0');
  return `KEY${year}${digits}`; // 3+4+13 = 20 chars
};

// ── EMI Plan sub-object ───────────────────────────────────────────────────────
class EMIPlan {
  constructor(data = {}) {
    this.productName       = data.productName       || '';
    this.productPrice      = data.productPrice      || 0;       // total price
    this.downPayment       = data.downPayment        || 0;
    this.interestRate      = data.interestRate       || 0;       // % per annum
    this.tenure            = data.tenure             || 1;       // number of periods
    this.tenureType        = data.tenureType         || 'monthly'; // weekly | monthly | yearly | custom
    this.customDays        = data.customDays         || 30;      // used if tenureType === 'custom'
    this.startDate         = data.startDate          || new Date().toISOString().split('T')[0];
    this.emiAmount         = data.emiAmount          || 0;       // computed or manual
    this.totalPayable      = data.totalPayable       || 0;
    this.totalInterest     = data.totalInterest      || 0;
    this.payments          = Array.isArray(data.payments) ? data.payments : [];
    this.status            = data.status             || 'Active'; // Active | Completed | Defaulted | Paused
  }

  // Calculate EMI amount based on plan
  calculate() {
    const principal = this.productPrice - this.downPayment;
    if (principal <= 0) { this.emiAmount = 0; this.totalPayable = this.downPayment; this.totalInterest = 0; return this; }

    if (this.interestRate === 0) {
      this.emiAmount    = +(principal / this.tenure).toFixed(2);
      this.totalPayable = +(this.downPayment + principal).toFixed(2);
      this.totalInterest = 0;
      return this;
    }

    // Standard EMI formula: EMI = P * r * (1+r)^n / ((1+r)^n - 1)
    let periodsPerYear;
    switch (this.tenureType) {
      case 'weekly':  periodsPerYear = 52; break;
      case 'yearly':  periodsPerYear = 1;  break;
      case 'custom':  periodsPerYear = Math.round(365 / (this.customDays || 30)); break;
      default:        periodsPerYear = 12; // monthly
    }

    const r = (this.interestRate / 100) / periodsPerYear;
    const n = this.tenure;
    const emi = principal * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);

    this.emiAmount     = +emi.toFixed(2);
    this.totalPayable  = +(this.downPayment + emi * n).toFixed(2);
    this.totalInterest = +(this.totalPayable - this.productPrice).toFixed(2);
    return this;
  }

  // Generate payment schedule
  generateSchedule() {
    this.calculate();
    const schedule = [];
    const start = new Date(this.startDate);

    let intervalDays;
    switch (this.tenureType) {
      case 'weekly':  intervalDays = 7;               break;
      case 'yearly':  intervalDays = 365;              break;
      case 'custom':  intervalDays = this.customDays;  break;
      default:        intervalDays = 30;               // monthly approx
    }

    let balance = this.productPrice - this.downPayment;

    for (let i = 1; i <= this.tenure; i++) {
      const dueDate = new Date(start);
      dueDate.setDate(dueDate.getDate() + intervalDays * i);

      const interest  = +(balance * (this.interestRate / 100) / 12).toFixed(2);
      const principal = +(this.emiAmount - interest).toFixed(2);
      balance = +Math.max(0, balance - principal).toFixed(2);

      schedule.push({
        installmentNo: i,
        dueDate:       dueDate.toISOString().split('T')[0],
        emiAmount:     this.emiAmount,
        principal,
        interest,
        balance,
        status:        'Pending',   // Pending | Paid | Overdue | Partial
        paidAmount:    0,
        paidDate:      null,
        notes:         '',
      });
    }

    this.payments = schedule;
    return this;
  }

  // Stats
  get paidInstallments()    { return this.payments.filter(p => p.status === 'Paid').length; }
  get overdueInstallments() { return this.payments.filter(p => p.status === 'Overdue').length; }
  get pendingInstallments() { return this.payments.filter(p => p.status === 'Pending').length; }
  get totalPaid()           { return +this.payments.reduce((s, p) => s + (p.paidAmount || 0), 0).toFixed(2); }
  get totalDue()            { return +(this.totalPayable - this.totalPaid - this.downPayment).toFixed(2); }
  get progressPercent()     { return this.tenure ? Math.round((this.paidInstallments / this.tenure) * 100) : 0; }
  get nextDue()             { return this.payments.find(p => p.status === 'Pending' || p.status === 'Overdue') || null; }
}

// ── Owner sub-object ──────────────────────────────────────────────────────────
class DeviceOwner {
  constructor(data = {}) {
    this.fullName        = data.fullName        || '';
    this.phoneNumber     = data.phoneNumber     || '';
    this.alternatePhone  = data.alternatePhone  || '';
    this.email           = data.email           || '';
    this.address         = data.address         || '';
    this.city            = data.city            || '';
    this.state           = data.state           || '';
    this.pincode         = data.pincode         || '';
    // Documents
    this.profileImage    = data.profileImage    || null; // URL
    this.aadharCard      = data.aadharCard      || null; // URL
    this.panCard         = data.panCard         || null; // URL
    this.aadharNumber    = data.aadharNumber    || '';
    this.panNumber       = data.panNumber       || '';
    // Digital signature
    this.digitalSignature = data.digitalSignature || null; // base64 or URL
    this.signedAt        = data.signedAt         || null;
  }

  validate() {
    const errors = [];
    if (!this.fullName?.trim())    errors.push('Owner name is required');
    if (!this.phoneNumber?.trim()) errors.push('Phone number is required');
    if (this.phoneNumber && !/^\d{10}$/.test(this.phoneNumber.replace(/\D/g,'')))
      errors.push('Phone number must be 10 digits');
    return { isValid: errors.length === 0, errors };
  }
}

// ── Main Device class ─────────────────────────────────────────────────────────
class Device {
  constructor(data = {}) {
    // ── Identity ───────────────────────────────────────────────────────────
    this.id           = data.id        || data._id    || null;
    this._id          = data._id       || data.id     || null;
    this.deviceKey    = data.deviceKey || generateId(); // KEY2026XXXXXXXXXXXXX — 20 chars
    this.deviceId     = data.deviceId  || this.deviceKey;

    // ── Owner ──────────────────────────────────────────────────────────────
    this.owner = new DeviceOwner(data.owner || {});

    // ── Device Info ────────────────────────────────────────────────────────
    this.deviceName    = data.deviceName    || '';
    this.serialNumber  = data.serialNumber  || '';
    this.platform      = data.platform      || 'Android';
    this.osVersion     = data.osVersion     || '';
    this.model         = data.model         || '';
    this.manufacturer  = data.manufacturer  || '';
    this.imei1         = data.imei1         || '';   // Primary IMEI
    this.imei2         = data.imei2         || '';   // Secondary IMEI (dual SIM)
    this.macAddress    = data.macAddress    || '';

    // ── Assignment ─────────────────────────────────────────────────────────
    this.assignedTo    = data.assignedTo    || '';
    this.assignedToId  = data.assignedToId  || null;
    this.department    = data.department    || '';
    this.group         = data.group         || '';

    // ── Status ─────────────────────────────────────────────────────────────
    this.status           = data.status           || 'Pending'; // Pending until enrolled
    this.complianceStatus = data.complianceStatus || 'Unknown';
    this.enrollmentDate   = data.enrollmentDate   || null;      // set on enrollment
    this.isEnrolled       = data.isEnrolled       ?? false;
    this.lastSeen         = data.lastSeen         || new Date().toISOString();

    // ── Hardware ───────────────────────────────────────────────────────────
    this.batteryLevel  = data.batteryLevel  ?? 100;
    this.storageUsed   = data.storageUsed   ?? 0;
    this.storageTotal  = data.storageTotal  ?? 64;
    this.isEncrypted   = data.isEncrypted   ?? true;
    this.isLocked      = data.isLocked      ?? false;
    this.location      = data.location      || '';
    this.ipAddress     = data.ipAddress     || '';
    this.policyName    = data.policyName    || '';
    this.notes         = data.notes         || '';

    // ── EMI Plan ───────────────────────────────────────────────────────────
    this.emiPlan = data.emiPlan ? new EMIPlan(data.emiPlan) : null;

    // ── Audit ──────────────────────────────────────────────────────────────
    this.createdAt = data.createdAt || new Date().toISOString();
    this.updatedAt = data.updatedAt || new Date().toISOString();
  }

  // ── Enrollment ─────────────────────────────────────────────────────────────
  enroll(deviceInfo = {}) {
    this.isEnrolled     = true;
    this.enrollmentDate = new Date().toISOString();
    this.status         = 'Active';
    // Device info comes in after enrollment
    Object.assign(this, deviceInfo);
    return this;
  }

  // ── Validation ─────────────────────────────────────────────────────────────
  validate() {
    const errors = [];

    // Owner validation
    const ownerVal = this.owner.validate();
    errors.push(...ownerVal.errors);

    if (!this.deviceName?.trim())   errors.push('Device name is required');
    if (!this.serialNumber?.trim()) errors.push('Serial number is required');
    if (!this.platform)             errors.push('Platform is required');
    if (!this.model?.trim())        errors.push('Model is required');

    return { isValid: errors.length === 0, errors };
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  getStatusColor() {
    return { Active:'#10b981', Inactive:'#6b7280', Lost:'#ef4444', Retired:'#f59e0b', Pending:'#3b82f6' }[this.status] || '#6b7280';
  }
  getComplianceColor() {
    return { Compliant:'#10b981', 'Non-Compliant':'#ef4444', Unknown:'#f59e0b' }[this.complianceStatus] || '#6b7280';
  }
  getPlatformColor() {
    return { Android:'#34a853', iOS:'#007aff', Windows:'#0078d4', macOS:'#555555' }[this.platform] || '#6b7280';
  }
  getStoragePercent() {
    return this.storageTotal ? Math.round((this.storageUsed / this.storageTotal) * 100) : 0;
  }
  getDaysSinceLastSeen() {
    return Math.floor((new Date() - new Date(this.lastSeen)) / 86400000);
  }

  toJSON() {
    return {
      ...this,
      owner:   { ...this.owner },
      emiPlan: this.emiPlan ? { ...this.emiPlan } : null,
    };
  }

  static fromJSON(json) { return new Device(json); }
}

export { EMIPlan, DeviceOwner };
export default Device;